import {
  createContext,
  useState,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useEffect,
} from "react";
import ToastContainer from "../components/ui/Toast.jsx";

/**
 * Toasts de la app — UN provider en la raíz, UN contenedor en pantalla.
 *
 * Usage:
 *   const { toast } = useToast();
 *   toast('Mensaje', 'warning');                            // tipo: 'info' | 'success' | 'warning' | 'error'
 *   toast('Mensaje', 'error', { icon: 'location_off' });    // ícono custom opcional
 *   toast('Mensaje', 'info', { duration: 3000 });           // duración custom
 *   toast('Mensaje', 'info', 3000);                         // legacy: tercer arg numérico = duration
 *   // Salida a mano cuando el aviso es un callejón sin salida (un permiso que
 *   // el sistema ya no vuelve a pedir): botón dentro del toast. El handler
 *   // tiene que ATENDER el resultado — `openAppSettings()` devuelve false en
 *   // web y en un ROM sin pantalla de ajustes, y un botón que no abre nada es
 *   // peor que no tenerlo.
 *   toast('Permiso denegado', 'error', {
 *     action: {
 *       label: 'Abrir ajustes',
 *       onClick: async () => {
 *         if (!(await openAppSettings())) toast('Ajustes → Aplicaciones → …', 'info');
 *       },
 *     },
 *     duration: 8000,
 *   });
 *
 * NO hay que renderizar nada: `<ToastProvider>` (montado en `App.jsx`) es el
 * único que monta el `ToastContainer`. Esto era antes estado LOCAL de React y
 * cada pantalla tenía que acordarse de pintar su propio contenedor; el que se
 * olvidaba se quedaba sin avisos y ningún test lo notaba (PR #645: ocho toasts
 * al vacío en la cola de visitas, y el fallo de guardado del panel de
 * inspección — el que el técnico necesita en la calle). Dos pantallas anidadas
 * que sí se acordaban daban el bug opuesto: dos contenedores `fixed` en la
 * misma esquina.
 */
// Tope de toasts simultáneos: una ráfaga (p.ej. una geocerca grande generando
// decenas de ENTER/EXIT en segundos) descartaba nada y apilaba todos en
// pantalla. Se conservan los más recientes.
const MAX_VISIBLE_TOASTS = 8;

// `null` como default es lo que hace ruidoso el olvido: `useToast()` fuera del
// provider TIRA en vez de devolver un objeto que se traga los mensajes.
const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timerRefs = useRef(new Set());

  const toast = useCallback((msg, type = "info", options = {}) => {
    const { duration = 4000, icon, action } =
      typeof options === "number" ? { duration: options } : options;
    const id = Date.now() + Math.random();
    setToasts((prev) =>
      [...prev, { id, msg, type, icon, action }].slice(-MAX_VISIBLE_TOASTS),
    );
    const handle = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      timerRefs.current.delete(handle);
    }, duration);
    timerRefs.current.add(handle);
  }, []);

  useEffect(
    () => () => {
      for (const h of timerRefs.current) clearTimeout(h);
      timerRefs.current.clear();
    },
    [],
  );

  // El valor NO lleva `toasts`: así nunca cambia de identidad y disparar un
  // toast no re-renderiza a los consumidores. Solo se re-renderiza este
  // provider — y `children` viene por prop, con la misma identidad de elemento,
  // así que React se saltea el subárbol. Antes cada `toast()` re-renderizaba la
  // pantalla dueña del estado (el mapa del monitor, entre otras).
  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      "useToast() requiere un <ToastProvider> ancestro (se monta en App.jsx). " +
        "Sin él los avisos no se ven: fallá acá y no en silencio.",
    );
  }
  return ctx;
}
